import { z } from 'zod';
import {
  fillPeriods,
  periodStart,
  RANGE_PATTERN,
  rangeStart,
  trendInterval,
  type WidgetCell,
  type WidgetData,
  type WidgetDataKind,
  type WidgetSeriesRow,
} from './widgets';

/**
 * How records (an action's output, a file) become what a widget shows: where they are, which
 * ones, and their count or sum per period and per group, or the columns of a table.
 */
export const widgetTransformSchema = z
  .object({
    /** Where the records are in the output: a dotted path (`data`, `result.items`). */
    path: z.string().max(256).optional(),
    /** Fields that must have a value (one of a list). */
    where: z.record(z.string(), z.union([z.unknown(), z.array(z.unknown())])).optional(),
    /** The records' time field (ISO date, or Unix seconds or milliseconds). */
    time: z.string().max(128).optional(),
    /** Only records whose time is within this range back from now. */
    since: z.string().regex(RANGE_PATTERN, 'A range is like 7d, 12w, 6m or 1y.').optional(),
    /** A series per period of `time`. */
    interval: z.enum(['day', 'week', 'month']).optional(),
    /** A row per value of this field. */
    groupBy: z.string().max(128).optional(),
    /** What a row's value is: the number of records (default), or a field's sum, mean, min, max. */
    aggregate: z.enum(['count', 'sum', 'avg', 'min', 'max']).optional(),
    field: z.string().max(128).optional(),
    /** Multiplies values (cents to units: 0.01). */
    scale: z.number().finite().optional(),
    /** Tables and lists: their order, their size and the fields they show. */
    sort: z
      .object({ field: z.string().max(128), order: z.enum(['asc', 'desc']).default('desc') })
      .strict()
      .optional(),
    limit: z.number().int().positive().max(500).optional(),
    columns: z.array(z.string().max(128)).max(12).optional(),
  })
  .strict()
  .refine((transform) => (transform.aggregate ?? 'count') === 'count' || transform.field, {
    message: 'A sum, mean, min or max needs its field.',
    path: ['field'],
  })
  .refine((transform) => transform.since === undefined || transform.time !== undefined, {
    message: 'since needs the time field.',
    path: ['time'],
  })
  .refine((transform) => transform.interval === undefined || transform.time !== undefined, {
    message: 'interval needs the time field.',
    path: ['time'],
  });
export type WidgetTransform = z.infer<typeof widgetTransformSchema>;

/** A dotted path's value (`user.login`, `items.0.title`). */
export function pick(value: unknown, path: string | undefined): unknown {
  if (path === undefined || path === '') return value;
  let current = value;
  for (const key of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/** A date from an ISO string or a Unix time in seconds or milliseconds. */
export function toDate(value: unknown): Date | null {
  if (typeof value === 'number' && Number.isFinite(value))
    return new Date(value < 1e12 ? value * 1000 : value);
  if (typeof value === 'string' && value.length > 0) {
    const date = /^\d+$/.test(value) ? toDate(Number(value)) : new Date(value);
    return date !== null && !Number.isNaN(date.getTime()) ? date : null;
  }
  return null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)))
    return Number(value);
  return null;
}

function cell(value: unknown): WidgetCell {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return value;
  if (Array.isArray(value))
    return value
      .map((item) =>
        item !== null && typeof item === 'object'
          ? String((item as Record<string, unknown>)['name'] ?? JSON.stringify(item))
          : String(item),
      )
      .join(', ');
  return JSON.stringify(value);
}

/** The records of an output: an array's items, an object alone, a number as `{ value }`. */
export function recordsOf(output: unknown, path: string | undefined): Record<string, unknown>[] {
  const value = pick(output, path);
  if (Array.isArray(value))
    return value.map((item) =>
      item !== null && typeof item === 'object'
        ? (item as Record<string, unknown>)
        : { value: item },
    );
  if (value === null || value === undefined) return [];
  if (typeof value === 'object') return [value as Record<string, unknown>];
  return [{ value }];
}

function matches(record: Record<string, unknown>, where: WidgetTransform['where']): boolean {
  for (const [field, wanted] of Object.entries(where ?? {})) {
    const value = pick(record, field);
    const allowed = Array.isArray(wanted) ? wanted : [wanted];
    if (!allowed.some((candidate) => candidate === value)) return false;
  }
  return true;
}

/** Parses CSV text (quoted fields, a header row) into records; numbers stay text. */
export function parseCsv(text: string): Record<string, string>[] {
  const [header, ...body] = parseCsvRows(text).filter((entry) =>
    entry.some((value) => value !== ''),
  );
  if (header === undefined) return [];
  return body.map((entry) => Object.fromEntries(header.map((name, at) => [name, entry[at] ?? ''])));
}

/**
 * CSV text as its rows of fields (quoted fields, CRLF or LF, a byte order mark ignored), split on
 * `separator` (a comma; a semicolon or a tab in some exports).
 */
export function parseCsvRows(source: string, separator: ',' | ';' | '\t' = ','): string[][] {
  const text = source.startsWith('\uFEFF') ? source.slice(1) : source;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (quoted) {
      if (char === '"' && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') quoted = false;
      else field += char;
    } else if (char === '"') quoted = true;
    else if (char === separator) {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += char;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * What a widget of `kind` shows of an output, as its transform says. `now` ends its `since`
 * range; records after it count only without `bounded` (the range before another).
 */
export function transformOutput(
  output: unknown,
  kind: WidgetDataKind,
  transform: WidgetTransform = {},
  now = new Date(),
  bounded = false,
): WidgetData {
  const since = transform.since === undefined ? null : rangeStart(transform.since, now);
  const records = recordsOf(output, transform.path).filter((record) => {
    if (!matches(record, transform.where)) return false;
    if (since === null) return true;
    const time = toDate(pick(record, transform.time)) ?? new Date(0);
    return time >= since && (!bounded || time < now);
  });
  if (kind === 'table' || kind === 'list') {
    const { sort } = transform;
    const sorted =
      sort === undefined
        ? records
        : records.toSorted((left, right) => {
            const a = pick(left, sort.field);
            const b = pick(right, sort.field);
            const order =
              toNumber(a) !== null && toNumber(b) !== null
                ? (toNumber(a) ?? 0) - (toNumber(b) ?? 0)
                : String(a ?? '').localeCompare(String(b ?? ''));
            return sort.order === 'asc' ? order : -order;
          });
    const columns =
      transform.columns ??
      Object.keys(sorted[0] ?? {})
        .filter((key) => cell(sorted[0]?.[key]) !== null)
        .slice(0, 6);
    return {
      kind: 'records',
      columns,
      total: sorted.length,
      records: sorted
        .slice(0, transform.limit ?? 50)
        .map((record) =>
          Object.fromEntries(columns.map((column) => [column, cell(pick(record, column))])),
        ),
    };
  }
  const aggregate = transform.aggregate ?? 'count';
  const buckets = new Map<
    string,
    { at?: string; group?: string; values: number[]; count: number }
  >();
  for (const record of records) {
    const date = transform.interval === undefined ? null : toDate(pick(record, transform.time));
    if (transform.interval !== undefined && date === null) continue;
    const at =
      date === null || transform.interval === undefined
        ? undefined
        : periodStart(date, transform.interval);
    const group =
      transform.groupBy === undefined
        ? undefined
        : String(cell(pick(record, transform.groupBy)) ?? '—');
    const key = `${at ?? ''}\n${group ?? ''}`;
    const bucket = buckets.get(key) ?? {
      ...(at === undefined ? {} : { at }),
      ...(group === undefined ? {} : { group }),
      values: [],
      count: 0,
    };
    bucket.count += 1;
    const value = transform.field === undefined ? null : toNumber(pick(record, transform.field));
    if (value !== null) bucket.values.push(value);
    buckets.set(key, bucket);
  }
  const bucketValue = (bucket: { values: number[]; count: number }) => {
    const { values } = bucket;
    switch (aggregate) {
      case 'count':
        return bucket.count;
      case 'sum':
        return values.reduce((sum, value) => sum + value, 0);
      case 'avg':
        return values.length === 0
          ? 0
          : values.reduce((sum, value) => sum + value, 0) / values.length;
      case 'min':
        return values.length === 0 ? 0 : Math.min(...values);
      case 'max':
        return values.length === 0 ? 0 : Math.max(...values);
    }
  };
  let rows: WidgetSeriesRow[] = [...buckets.values()].map((bucket) => ({
    ...(bucket.at === undefined ? {} : { at: bucket.at }),
    ...(bucket.group === undefined ? {} : { group: bucket.group }),
    value: bucketValue(bucket) * (transform.scale ?? 1),
  }));
  // The largest groups over the whole range; then periods in order.
  if (transform.groupBy !== undefined) {
    const totals = new Map<string, number>();
    for (const row of rows)
      totals.set(row.group ?? '', (totals.get(row.group ?? '') ?? 0) + row.value);
    const kept = new Set(
      [...totals.entries()]
        .toSorted((left, right) => right[1] - left[1])
        .slice(0, transform.limit ?? 8)
        .map(([group]) => group),
    );
    rows = rows.filter((row) => kept.has(row.group ?? ''));
  }
  if (transform.interval !== undefined)
    rows = fillPeriods(
      rows,
      transform.interval,
      now,
      transform.since === undefined ? undefined : rangeStart(transform.since, now),
    );
  rows.sort(
    (left, right) =>
      (left.at ?? '').localeCompare(right.at ?? '') ||
      (transform.interval === undefined ? right.value - left.value : 0) ||
      (left.group ?? '').localeCompare(right.group ?? ''),
  );
  // A total over nothing is still a value.
  if (rows.length === 0 && transform.interval === undefined && transform.groupBy === undefined)
    rows = [{ value: 0 }];
  return { kind: 'series', rows };
}

/**
 * An output as a widget of `kind` shows it, as its transform says; a stat over a range (`since`)
 * also gets its trend and its value over the range before.
 */
export function transformRecords(
  output: unknown,
  kind: WidgetDataKind,
  transform: WidgetTransform | undefined,
  now = new Date(),
): WidgetData {
  const data = transformOutput(output, kind, transform, now);
  if (kind !== 'stat' || data.kind !== 'series' || transform?.since === undefined) return data;
  const interval = trendInterval(transform.since, now);
  const series = transformOutput(
    output,
    'line',
    { ...transform, groupBy: undefined, interval },
    now,
  );
  // The range before: the same records, its window moved back by its length.
  const span = now.getTime() - rangeStart(transform.since, now).getTime();
  const before = transformOutput(output, 'stat', transform, new Date(now.getTime() - span), true);
  return {
    ...data,
    ...(series.kind === 'series' ? { trend: series.rows.map((row) => row.value) } : {}),
    ...(before.kind === 'series'
      ? { previous: before.rows.reduce((sum, row) => sum + row.value, 0) }
      : {}),
  };
}
